/**
 * Gemeinsamer Vertrag zwischen Frontend (apps/frontend), Backend (apps/backend) und den
 * n8n-Workflows.
 *
 * Diese Datei ist der Grund, warum Frontend und Backend in einem Repository liegen: der
 * Entscheidungs-Payload aus AP4 wird von beiden Seiten benutzt, und zwei Kopien davon
 * laufen garantiert auseinander.
 *
 * Quelle der Festlegungen: docs/ARCHITEKTUR.md sowie im n8n-Projektverzeichnis
 * `docs/frontend-umbau-plan-und-aufwand.md` (Abschnitt "Vorgemerkt: Annotationen im HITL").
 */

// ---------------------------------------------------------------------------
// Auftraege
// ---------------------------------------------------------------------------

/** Die vier Eingaenge der Pipeline. `ratgeber` traegt die Sprache separat (siehe RatgeberStart). */
export type AuftragsTyp = 'ratgeber' | 'bilder' | 'analyse' | 'pdp';

export type Sprache = 'de' | 'en';

/**
 * Statusmodell aus AP3. Bewusst ein Feld mit festen Werten, KEINE Ableitung aus Texten:
 * das TEF-Frontend leitet seine Phase aus Bot-Nachrichten ab, das ist fragil und wird
 * hier nicht uebernommen.
 */
export type AuftragsStatus =
  | 'angelegt'
  | 'recherche'
  | 'entwurf'
  | 'wartet_auf_freigabe'
  /**
   * Nur `pdp`: Die Analyse ist fertig, ein Mensch entscheidet, ob optimiert wird (Spec
   * 2026-10-05-2c-teil2, Abschnitt 3). Wie `wartet_auf_freigabe` ein Wartestatus -- der Sweep
   * bricht ihn nie ab.
   */
  | 'wartet_auf_entscheidung'
  | 'in_ueberarbeitung'
  | 'finalisierung'
  | 'fertig'
  | 'fehlgeschlagen'
  | 'abgebrochen';

/**
 * Das Lesemodell in Firestore. Bewusst schlank: die n8n Data Tables bleiben der
 * Arbeitsspeicher der Workflows, hier steht nur, was das Frontend anzeigen muss.
 * Grund fuer Firestore ist `onSnapshot` (Live-Status ohne Polling) und die Security Rules,
 * nicht die Datenhaltung an sich.
 */
/**
 * Was die PDP-Analyse am Entscheidungstor meldet (Spec 2026-10-05-2c-teil2, Abschnitt 4.1).
 * `auditId` ist die Klammer zur Audit-Zeile in n8n -- ohne sie gibt es nichts zu optimieren,
 * `keinBedarf` beendet den Auftrag ohne Tor.
 */
export interface PdpBefund {
  url: string;
  h1: string;
  grund: string;
  empfehlungen?: number;
  auditId?: string;
  keinBedarf?: boolean;
  hinweis?: string;
  /** Zahlen je Block fuer die Kurzkarte (Spec 2026-10-07, Abschnitt 4.1). Fehlt bei aelteren Auftraegen. */
  bloecke?: PdpBlockZahlen;
  /** Wie bei `AnalyseSeite`: Urteil der Herkunfts-Pruefung. Fehlt bei aelteren Auftraegen. */
  keywordHinweis?: KeywordHinweis;
  /** Nur bei `abweichend`: das Fokus-Keyword, das nicht zur Seite passt. */
  fokusKeyword?: string;
  /**
   * Nur bei `keinBedarf`: was der Vorfilter geprueft hat (Spec 2026-11-03-kein-bedarf, 4.1). Fehlt
   * bei Altauftraegen; dann bleibt `hinweis` der einzige Satz.
   */
  pruefung?: PdpPruefung;
}

/** Wie belastbar die Suchdaten hinter der Sichtbarkeitspruefung sind. */
export type PdpDatenlage = 'belastbar' | 'duenn' | 'keine_daten';

export const PDP_DATENLAGEN: readonly PdpDatenlage[] = ['belastbar', 'duenn', 'keine_daten'];

/**
 * Die Pruefpunkte des Vorfilters bei einer PDP ohne Optimierungsbedarf (Spec 2026-11-03-kein-bedarf,
 * Abschnitt 3 und 4.1). Ein Feld fehlt, wenn n8n es im Lauf nicht ermittelt hat -- `null` heisst
 * dagegen "ermittelt, aber ohne Wert" (etwa keine passende Suchanfrage).
 */
export interface PdpPruefung {
  suchwort?: string;
  besteAnfrage?: string | null;
  /** Durchschnittliche Position der besten Anfrage (Ziel: Platz 8 oder besser). */
  position?: number | null;
  impressionen?: number | null;
  datenlage?: PdpDatenlage;
  h1Passt?: boolean;
  /** Stand-Datum der Seite (TT.MM.JJJJ), `null`, wenn keines gefunden wurde. */
  standDatum?: string | null;
  /** Jahreszahlen vor dem Vorjahr im Text; leer, wenn keine gefunden wurden. */
  veralteteJahre?: string[];
}

/** Stand einer Seite in einer Ratgeber-Analyse. n8n meldet immer `offen`, den Rest setzt das Backend. */
export type AnalyseSeitenStand = 'offen' | 'optimiert' | 'verworfen' | 'ueberholt';

/** Obergrenze je Liste im `analyseBefund` (Spec 2026-10-13-ratgeber-analyse-frontend, 4.1). */
export const MAX_ANALYSE_SEITEN = 150;

/**
 * Urteil der Herkunfts-Pruefung in n8n (`Generate audit_id`, Regel vom 2026-10-16) ueber das
 * Fokus-Keyword einer Seite. `abweichend`: Die SEO-Daten gehoeren nachweislich zur Seite, aber ein
 * Kernwort des Keywords steht weder in URL, H1 noch Produkt -- die Empfehlungen sind da, aber zu
 * pruefen. `fehlt`: Der SEO-Subflow hat kein Fokus-Keyword geliefert -- es gibt keine Empfehlungen,
 * und 1b bricht eine Optimierung ab. Fehlt das Feld, war nichts auffaellig (oder Altbestand).
 */
export type KeywordHinweis = 'abweichend' | 'fehlt';

export const KEYWORD_HINWEISE: readonly KeywordHinweis[] = ['abweichend', 'fehlt'];

/** Eine Seite mit Optimierungsbedarf aus einer Ratgeber-Analyse. */
export interface AnalyseSeite {
  auditId: string;
  url: string;
  h1: string;
  grund: string;
  empfehlungen?: number;
  bloecke?: PdpBlockZahlen;
  stand: AnalyseSeitenStand;
  /** Nur `optimiert`: der neue Ratgeber-Auftrag. */
  kindRunId?: string;
  /** Die Entscheidung je Seite (fehlt bei `offen` und `ueberholt`). */
  entscheidung?: PdpEntscheidung;
  entschiedenAm?: string;
  /**
   * Nur `ueberholt`: die neuere Analyse DERSELBEN Person, die diese Seite erneut geprueft hat.
   * Fehlt bei Altbestand, der ueber den 404 des Lesestrangs ueberholt wurde -- dort sind die
   * Analysedaten weg, hier bleiben sie einsehbar (Entscheidung Martin 2026-10-16).
   */
  ueberholtDurch?: string;
  keywordHinweis?: KeywordHinweis;
  /** Das Fokus-Keyword der Recherche, damit der Hinweis `abweichend` es nennen kann. */
  fokusKeyword?: string;
}

/** Eine gepruefte Seite ohne Optimierungsbedarf. */
export interface AnalyseGeprueft {
  url: string;
  h1: string;
  grund: string;
  /** Stand-Datum der Seite laut Vorfilter (`Check Datum2`), Form TT.MM.JJJJ. Fehlt, wenn unbekannt. */
  standDatum?: string;
  /**
   * Kind-Auftrag "volle Analyse" fuer diese Seite (Spec 2026-11-03-kein-bedarf, 4.2). Gehoert allein
   * dem Backend (`legeVolleAnalyseAn`), kommt nie aus einer n8n-Meldung. Regel fuer Button oder Link
   * wie am PDP-Ursprung: `volleAnalyseKindErsetzbar`.
   */
  kindRunId?: string;
}

/**
 * Was eine Ratgeber-Analyse aus dem Frontend am Ende meldet (Spec 2026-10-13-ratgeber-analyse-frontend).
 * Gekuerzt auf MAX_ANALYSE_SEITEN je Liste; was wegfaellt, zaehlt in `*Weitere`.
 */
export interface AnalyseBefund {
  seiten: AnalyseSeite[];
  geprueft: AnalyseGeprueft[];
  seitenWeitere?: number;
  gepruefteWeitere?: number;
  /**
   * Wann das Backend diese Liste angenommen hat (`verschmelze`). Ordnet Analysen derselben Seite:
   * Die spaeter eingetroffene gewinnt, auch wenn zwei parallel laufen. Fehlt bei Altbestand; dann
   * gilt `angelegtAm` (`listeEingegangenAm` in apps/backend/src/status.ts).
   */
  eingegangenAm?: string;
}

/** Die zwei Antworten am Entscheidungstor eines PDP-Auftrags. */
export type PdpAktion = 'optimieren' | 'verwerfen';

/** Hoechstlaenge des Zusatzhinweises am PDP-Tor -- dieselbe Grenze, auf die 1b kuerzt (`Audit-Treffer pruefen`). */
export const MAX_PDP_HINWEIS_ZEICHEN = 1200;

/** A = eigener Abschnitt, B = interner Link auf bestehende ERGO-Seite, C = verworfen (Martins Phase 6). */
export type AbcKlasse = 'A' | 'B' | 'C';

export interface AbcVerteilung {
  a: number;
  b: number;
  c: number;
}

export interface PdpBlockZahlen {
  keywords: number;
  ueberschriften: number;
  entities: number;
  inhalte: number;
  usps: number;
  faq: number;
  klassen?: { keywords?: AbcVerteilung; entities?: AbcVerteilung; inhalte?: AbcVerteilung };
}

/** Was am Tor entschieden wurde. Liegt am Auftrag, nicht am Befund -- den ersetzt jede neue Meldung. */
export interface PdpEntscheidung {
  aktion: PdpAktion;
  zusatzhinweis: string;
  sharepoint: boolean;
  am: string;
  /** Gesetzt, wenn 1b den Hinweis per Regex-Filter oder LLM-Guard nicht verwendet hat. */
  hinweisVerworfen?: boolean;
}

/** Die Angaben, die "Optimieren" zusaetzlich mitschickt. */
export interface PdpEntscheidungsAngaben {
  zusatzhinweis: string;
  sharepoint: boolean;
}

export interface PdpGap {
  titel: string;
  status?: string;
  klasse?: AbcKlasse;
  begruendung?: string;
  linkziel?: { url: string; h1?: string };
}

export interface PdpUeberschrift {
  thema: string;
  keywordVorschlag?: string;
}

export interface PdpFundstelle {
  typ?: 'text' | 'pdf';
  pdfDokument?: string;
  abschnitt?: string;
  lage?: string;
  zitat?: string;
}

export interface PdpUsp {
  merkmal: string;
  status: string;
  beleg?: string;
  empfehlung?: string;
  fundstelle?: PdpFundstelle;
  differenzierungsgrad?: string;
  fehltBei: string[];
  /** Wettbewerber, gegen die ERGO besser ist (Leistungsvorsprung), nicht Gleichstand. */
  auchBei: string[];
  vergleich?: string;
  /** Urspruenglicher Status, wenn der Beleg-Guard ihn zurueckgestuft hat. */
  korrigiertVon?: string;
}

export interface PdpWettbewerber {
  domain: string;
  titel?: string;
  url?: string;
  rang?: number;
}

export interface PdpFaq {
  frage: string;
  signal?: string;
}

/** Eine PAA-Frage, die die Analyse geprueft, aber nicht als FAQ-Chance aufgenommen hat. */
export interface PdpFaqVerworfen {
  frage: string;
  /** Kategorie aus der Analyse (z.B. `bereits_in_faq`); die Oberflaeche uebersetzt sie. */
  grund: string;
  begruendung?: string;
}

export interface PdpRankingWert {
  keyword: string | null;
  position: number | null;
  impressions: number | null;
  klicks: number | null;
}

/** Aus der Spalte `Rankinganalyse` -- Grundlage fuer Martins `renderRankingHtml`. */
export interface PdpSuchdaten {
  h1: string;
  h1PasstZumKeyword: boolean | null;
  ausloeser: boolean;
  analyseText?: string;
  top3: PdpRankingWert[];
  nonbrand: Array<PdpRankingWert & { keineDaten: boolean }>;
  gscHinweis?: string;
  h1Ranking: PdpRankingWert | null;
  produktName: string;
  produktRanking: PdpRankingWert | null;
  aliase: Array<{ alias: string; ranking: PdpRankingWert | null }>;
  fehlRanking?: { produkt: string; url?: string };
}

export interface PdpKannibale {
  rang: number | null;
  url: string;
  titel?: string;
  herkunft?: string;
  istZiel: boolean;
}

export interface PdpKannibalisierung {
  fokusKeyword?: string;
  risiko: boolean;
  eintraege: PdpKannibale[];
}

export interface PdpRanking {
  suche?: PdpSuchdaten;
  kannibalisierung?: PdpKannibalisierung;
}

/** Antwort von `GET /runs/:id/befund-details`, vom Backend normalisiert (Spec 2026-10-07, 4.1). */
export interface PdpDetails {
  originaltext: string;
  empfehlungenUnlesbar?: true;
  ranking?: PdpRanking;
  keywords: PdpGap[];
  ueberschriften: PdpUeberschrift[];
  entities: PdpGap[];
  inhalte: PdpGap[];
  usps: { offen: PdpUsp[]; platziert: PdpUsp[]; schwach: PdpUsp[] };
  wettbewerber: PdpWettbewerber[];
  faq: PdpFaq[];
  /**
   * Optional statt Pflicht: Analysen vor dem 2026-10-23 liefern die Liste nicht, und bestehende
   * Fixtures bleiben gueltig. Das Backend setzt sie immer, notfalls leer.
   */
  faqVerworfen?: PdpFaqVerworfen[];
}

/** Anlass einer Benachrichtigungs-Mail aus dem Backend (Spec 2026-10-12, Abschnitt 5.4; `fertig`: Spec 2026-10-13). */
export type BenachrichtigungsAnlass = 'freigabe' | 'entscheidung' | 'fertig';

/**
 * Der Status, in dem ein Merker seines Anlasses gilt. `verschmelze` (status.ts) nimmt einen
 * Merker nur mit, wenn der Auftrag vorher UND nachher in diesem Status steht -- eine wiederholte
 * Meldung behaelt ihn, jede Zwischenstation verwirft ihn.
 */
export const BENACHRICHTIGUNGS_ZIELSTATUS: Readonly<
  Record<BenachrichtigungsAnlass, AuftragsStatus>
> = {
  freigabe: 'wartet_auf_freigabe',
  entscheidung: 'wartet_auf_entscheidung',
  fertig: 'fertig',
};

/**
 * Merker "fuer diesen Anlass und diese Runde ist die Mail beansprucht bzw. verschickt"
 * (`benachrichtigung.ts`). Zweistufig: transaktional VOR dem Versand als Anspruch gesetzt,
 * nach erfolgreichem Versand um `versendet: true` ergaenzt, bei einem Versandfehler wieder
 * entfernt. Nur ein Merker MIT `versendet` zaehlt als "benachrichtigt" (Spec 5.4.4). Ein
 * Anspruch ohne `versendet`, der aelter als zwei Minuten ist, gilt als verwaist und wird neu
 * vergeben. Fuer `entscheidung` und `fertig` ist `runde` immer 0 -- das PDP-Tor hat genau eine
 * Runde, und fertig wird ein Auftrag einmal.
 *
 * Gilt nur, solange der Auftrag im Zielstatus seines Anlasses steht
 * (`BENACHRICHTIGUNGS_ZIELSTATUS`; `verschmelze` in status.ts verwirft ihn beim Statuswechsel):
 * n8n legt nach einer Entscheidung per Mail-Link mit DERSELBEN Rundennummer vor.
 */
export interface BenachrichtigungsMerker {
  anlass: BenachrichtigungsAnlass;
  runde: number;
  /** ISO-Zeitpunkt des Anspruchs. */
  am: string;
  /** Erst nach erfolgreichem Versand gesetzt. */
  versendet?: true;
}

export interface Auftrag {
  /**
   * Stabile, eindeutige, URL-sichere Kennung. BEWUSST nicht "UUID": Ratgeber- und
   * Bilder-Kette erzeugen eine UUID, die Analyse-Kette behaelt ihr vorhandenes Format
   * `<executionId>-<timestamp>`. Frontend und Backend stuetzen sich auf die Eigenschaft,
   * nicht auf das Format -- acht funktionierende n8n-Nodes fuer Kosmetik anzufassen
   * waere ein schlechter Tausch (Entscheidung 2026-09-03).
   */
  runId: string;
  typ: AuftragsTyp;
  status: AuftragsStatus;
  /** Firebase-uid der Person, die den Auftrag gestartet hat. Basis des Datenschnitts. */
  ownerUid: string;
  /**
   * Zweiter Datenschnitt neben `ownerUid`: `'ergo'`, wenn die Person mit einer ergo.de-Adresse
   * angelegt hat (`mandantVon`). Darauf stuetzt sich die Gesamtansicht der Rolle `admin`, in
   * Firestore-Rules UND Backend. Fehlt bei allen anderen Adressen, also auch bei elaboratum --
   * deren Auftraege bleiben fuer `admin` unsichtbar (nur `technik` sieht sie, siehe `Rolle`). Gesetzt nur vom Backend aus dem
   * geprueften Token, nie aus einer Eingabe; Altbestand traegt `tools/mandant-nachtragen.js`
   * nach.
   */
  mandant?: Mandant;
  /** Adresse der anlegenden Person, fuer die Spalte "Angelegt von" der Gesamtansicht. */
  ownerEmail?: string;
  /** Anzeigename der anlegenden Person (`displayName`), falls gesetzt. */
  ownerName?: string;
  /**
   * Gesetzt statt Loeschen (Entscheidung 2026-10-16): Ein "geloeschter" Auftrag bleibt als Dokument
   * stehen, damit er weiter zur Monatsnutzung zaehlt (Codex-Gate PR #91: Loeschen senkte die
   * Nutzung rueckwirkend). Listen, Detailseiten und Leserouten behandeln ihn als nicht vorhanden;
   * nur die Nutzungsstatistik der Gesamtansicht zaehlt ihn. Gesetzt nur vom Backend.
   */
  ausgeblendetAm?: string;
  thema: string;
  angelegtAm: string;
  aktualisiertAm: string;
  /** Laufende Nummer der Feedback-Runde, beginnt bei 1. */
  runde?: number;
  /**
   * Die zuletzt TRANSAKTIONAL beanspruchte Runde -- gesetzt von `beanspracheRunde` in
   * `auftraege.ts`, bevor der Entscheidungs-POST an n8n rausgeht. Das, und nicht der
   * Vergleich von `status`/`runde` allein, ist der eigentliche Schutz gegen den doppelten
   * Klick: Nach einer Freigabe braucht n8n rund 66 Sekunden, bis `status`/`runde`
   * weitergeschrieben sind (Spec Abschnitt 4) -- in diesem Fenster wuerden zwei Klicks
   * dieselbe Pruefung unabhaengig voneinander bestehen, ohne dieses Feld. Bewusst NICHT in
   * `ANTWORT_FELDER` (siehe runs.ts): ein reines Backend-Steuerfeld, das Frontend braucht
   * es nicht und soll es nicht als vermeintlichen Anzeigewert missdeuten.
   *
   * NACHTRAG 2026-09-10: Seit die Firestore-Rules Lesezugriff fuer den Eigentuemer
   * freigeben, liest das Frontend das ROHE Dokument und sieht dieses Feld technisch mit.
   * `ANTWORT_FELDER` schuetzt nur den API-Weg. Dass das Frontend es nicht auswertet, ist
   * damit eine Konvention -- siehe firestore.rules.
   *
   * NACHTRAG 2026-09-29: Das Frontend liest es, aber AUSSCHLIESSLICH ueber
   * `offeneRunde` (unten) und die Helfer in apps/frontend/src/runs/uebergabe.ts;
   * Display/Entscheidungslogik darf es nicht direkt interpretieren.
   */
  entschiedeneRunde?: number;
  /**
   * Die Runde, zu der n8n zuletzt eine Fassung vorgelegt hat. Gesetzt von `verschmelze`
   * (apps/backend/src/status.ts) bei JEDER Meldung `wartet_auf_freigabe`, fuer Altbestand
   * nachgezogen von `erhoeheRunde`. Ohne dieses Feld sah eine Runde direkt nach "Ueberarbeiten"
   * (runde schon N+1, status noch `wartet_auf_freigabe`) entscheidbar aus -- mit dem ALTEN Text
   * (Beleg 2026-09-29, Spec 2026-09-29-absende-rennen Sec1). Reines Backend-Steuerfeld; das
   * Frontend nutzt es ueber `offeneRunde` (Sperre der Freigabeseite).
   */
  vorgelegteRunde?: number;
  ergebnis?: AuftragsErgebnis;
  fehler?: string;
  /**
   * Der Audit, aus dem dieser Auftrag hervorgegangen ist. Ein Optimierungslauf aus einem
   * Audit ist ein EIGENER Auftrag mit eigener runId -- aus einem Analyse-Lauf koennen n
   * Ratgeber-Laeufe werden. Diese Verbindung ist die einzige Klammer dazwischen.
   */
  ausAuditId?: string;
  /** Nur `pdp`: Ergebnis der Analyse, gesetzt von der Meldung am Ende der Analyse-Kette. */
  befund?: PdpBefund;
  /**
   * Nur `analyse` (Spec 2026-10-13-ratgeber-analyse-frontend): Seitenliste der Analyse. Der Stand je
   * Seite gehoert dem Backend (`entscheideAnalyseSeite`), n8n meldet die Liste genau einmal.
   */
  analyseBefund?: AnalyseBefund;
  /**
   * Kind-Auftrag aus einer Ratgeber-Analyse: die runId der Analyse (Ruecklink). Ebenso beim Kind
   * "volle Analyse" eines PDP-Auftrags ohne Bedarf: dann die runId dieses PDP-Auftrags.
   */
  ausAnalyseRunId?: string;
  /**
   * Nur am PDP-Auftrag mit `befund.keinBedarf` (Spec 2026-11-03-kein-bedarf, 4.2): die runId des
   * Kind-Auftrags, der die volle Analyse doch noch durchfuehrt. Gesetzt von `legeVolleAnalyseAn`;
   * ist das Kind `fehlgeschlagen`, ersetzt der naechste Klick die Kennung.
   */
  volleAnalyseRunId?: string;
  /**
   * Der Lauf hebelt den Vorfilter "kein Bedarf" aus (n8n-Flag `bedarf_erzwingen`). Steht am
   * Kind-Auftrag selbst; die Startnutzlast wird aus `kind.bedarfErzwingen` gebaut (Spec 7a).
   */
  bedarfErzwingen?: true;
  /**
   * Die Entscheidung am Tor (`pdp`, geschrieben von `entscheidePdp`) -- oder bei einem Kind-Auftrag
   * aus einer Analyse die Entscheidung, aus der er hervorging (Hinweis, SharePoint, `hinweisVerworfen`).
   */
  pdpEntscheidung?: PdpEntscheidung;
  /**
   * Reines Backend-Steuerfeld wie `entschiedeneRunde`: verhindert die zweite Mail bei einer
   * doppelten Statusmeldung (Spec 2026-10-12, Abschnitt 6). Bewusst NICHT in `ANTWORT_FELDER`.
   * Lebt nur im Wartezustand seines Anlasses, siehe `BenachrichtigungsMerker`.
   */
  benachrichtigungen?: BenachrichtigungsMerker[];
  /**
   * Das Briefing, mit dem der Auftrag gestartet wurde: die geprueften Angaben der Startanfrage
   * (`pruefeStartAnfrage`) ohne den Typ. Die Fortschrittsseite zeigt es wie die Content-Factory
   * ("Ihr Briefing fuer diesen Auftrag"). Optional, weil Auftraege von vor dem 2026-10-13 es
   * nicht tragen; dann zeigt die Seite nur das Thema.
   */
  briefing?: Briefing;
  /** Append-only: jeder Statuswurf haengt einen Eintrag an, damit die Abfolge nachvollziehbar bleibt. */
  verlauf?: Array<{ status: AuftragsStatus; zeitpunkt: string }>;
  /**
   * Zwischenstand der Bearbeitung, solange der Lauf auf eine Entscheidung wartet. Liegt
   * serverseitig, damit ein Geraete- oder Browserwechsel die Arbeit nicht verliert
   * (Entscheidung 2026-09-15). Gelesen wird er ueber den bestehenden Firestore-Snapshot,
   * geschrieben ueber `PUT /runs/:id/entwurf`.
   */
  entwurf?: {
    text: string;
    gespeichertAm: string;
    /**
     * Ruling 34: die Segmentierung von `text` zum Zeitpunkt des Speicherns -- ersetzt die
     * heuristische Ankersuche im Frontend (`schneideAnUrsprung`), die eine im eigenen
     * Inhalt wiederholte Ueberschrift nicht zuverlaessig von der echten unterscheiden
     * konnte. Jeder Eintrag entspricht GENAU EINEM Segment von `text`, in Reihenfolge:
     * `laenge` die Zeichenlaenge seines (zeilenende-normalisierten) Markdown-Textes,
     * `abschnittIds` die `RatgeberAbschnitt.abschnittId`s, aus denen es hervorgegangen ist
     * (normalerweise genau eine, mehr als eine nur bei einer Verschmelzung, siehe
     * `EntwurfSegmentEintrag`). Optional, weil ein VOR diesem Ruling gespeicherter Entwurf
     * das Feld nicht traegt -- das Frontend faellt dann auf die alte Ankersuche zurueck.
     */
    segmentierung?: EntwurfSegmentEintrag[];
    /**
     * Die Anmerkungen dieser Runde. Liegen im Zwischenstand, damit ein Geraete- oder
     * Browserwechsel sie nicht verliert -- dieselbe Begruendung wie fuer `text`. Sie
     * verschwinden von selbst mit ihrer Runde, weil `runs-route.ts` den Zwischenstand
     * loescht, sobald n8n die Entscheidung angenommen hat.
     *
     * Geschrieben wird sie immer als Liste, auch leer (AGENTS.md Regel 5). Optional ist sie nur,
     * damit aeltere Entwuerfe ohne das Feld lesbar bleiben.
     */
    anmerkungen?: Anmerkung[];
  };
}

/** Ein Eintrag in `Auftrag['entwurf']['segmentierung']`, siehe dort. */
export interface EntwurfSegmentEintrag {
  abschnittIds: string[];
  laenge: number;
}

export interface AuftragsErgebnis {
  vorschauUrl?: string;
  docxUrl?: string;
  driveOrdnerUrl?: string;
  bildUrls?: string[];
}

// ---------------------------------------------------------------------------
// Human in the Loop: der Entscheidungs-Payload
// ---------------------------------------------------------------------------

/**
 * Ein Anker auf eine Textstelle -- BEWUSST DOPPELT.
 *
 * - `zitat` samt Kontext davor und danach: davon lebt der LLM-Prompt. Er findet die Stelle
 *   auch dann noch, wenn sich der Text ringsum geaendert hat.
 * - `von`/`bis`: davon lebt die Oberflaeche, um die Markierung wieder anzuzeigen. Zeichen-
 *   Offsets im GERENDERTEN TEXT DES ABSCHNITTS, nicht im ganzen Dokument und nicht im
 *   Markdown. Sie gelten nur innerhalb einer Runde -- danach ist die Anmerkung erledigt.
 *
 * `abschnittId` bindet den Anker an seinen Abschnitt. `absatzNummer` ist reine Information
 * fuer den Prompt: die 1-basierte Nummer des Blockelements im gerenderten Abschnitt, das den
 * BEGINN der Markierung enthaelt. Sie wird aus dem DOM gezaehlt, nicht aus
 * `ratgeber_struktur` gelesen -- dessen `absaetze` entstehen in n8n ueber `puffer.join(' ')`
 * und zaehlen Listen und Tabellen anders, als `marked` sie rendert.
 */
export interface TextAnker {
  zitat: string;
  kontextDavor: string;
  kontextDanach: string;
  abschnittId: string;
  absatzNummer: number;
  von: number;
  bis: number;
}

export type AnmerkungsHerkunft = 'redakteur' | 'fachbereich';

/**
 * `herkunft` traegt heute immer `'redakteur'`, und die Oberflaeche zeigt sie nicht an -- es
 * gibt nur eine Quelle. Das Feld steht trotzdem von Anfang an im Vertrag: Der
 * Fachbereichs-Ruecklauf braucht genau diese Unterscheidung, und ein Feld in bereits
 * gespeicherte Strukturen nachzuruesten ist der teure Weg.
 */
export interface Anmerkung {
  anker: TextAnker;
  kommentar: string;
  herkunft: AnmerkungsHerkunft;
  autor?: string;
}

export type Entscheidung = 'approve' | 'optimize';

/**
 * Hoechstens fuenf Korrekturrunden je Lauf (Entscheidung 2026-09-15). Hier statt in
 * `apps/backend/src/runs.ts` allein, weil das Frontend denselben Wert braucht, um die
 * "Korrigieren"/"Optimieren"-Option ab dieser Grenze gar nicht erst anzubieten
 * (`apps/frontend/src/runs/freigabe-page.tsx`) -- zwei getrennte Kopien liefen genau wie
 * beim `EntscheidungsPayload` selbst garantiert auseinander (Codex-Gate-Befund MITTEL,
 * fuenfte Runde: eine spaetere Aenderung des Limits an nur einer Stelle liesse Anzeige und
 * Servervalidierung driften). `pruefeEntscheidung` weist `aktion: 'optimize'` erst ab, wenn
 * die Runde diesen Wert UEBERSCHREITET (`runde > MAX_KORREKTURRUNDEN`) -- die Grenzrunde
 * selbst darf noch korrigiert werden.
 */
export const MAX_KORREKTURRUNDEN = 5;

/** Grenzen der Anmerkungen. Uebernommen aus `hil2-validierung.ts` der `ergo-content-factory`,
 *  damit der spaetere Fachbereichs-Ruecklauf dieselben hat. */
export const MAX_ANMERKUNGEN = 20;
export const MAX_KOMMENTAR_ZEICHEN = 2000;
export const MAX_ZITAT_ZEICHEN = 2000;
export const MAX_KONTEXT_ZEICHEN = 500;

/**
 * Der Payload, den das Frontend beim Freigeben oder Ueberarbeiten schickt.
 *
 * ACHTUNG, das ist der wichtigste Vertrag im Projekt: `anmerkungen` ist eine LISTE, obwohl
 * das Frontend zunaechst immer nur `gesamtFeedback` fuellt und die Liste leer laesst.
 *
 * Annotationen -- einzelne Textstellen markieren und je Stelle kommentieren -- sind ein
 * Kundenwunsch vom 02.09.2026, der ausdruecklich NICHT Teil dieses Auftrags ist. Die Liste
 * jetzt schon vorzusehen kostet nichts und macht die spaetere Nachruestung zu einer
 * Frontend- und Prompt-Aenderung statt zu einer Schnittstellenaenderung, die alle
 * Beteiligten anfassen muesste.
 *
 * Wer hier ein einzelnes Feld daraus macht, weil die Liste "ja immer leer ist", hat die
 * Entscheidung gegen Annotationen getroffen, ohne sie zu treffen.
 */
export interface EntscheidungsPayload {
  runId: string;
  /** Schuetzt gegen Doppelklick und gegen das Entscheiden einer bereits erledigten Runde. */
  runde: number;
  aktion: Entscheidung;
  gesamtFeedback: string;
  anmerkungen: Anmerkung[];
  /**
   * Bildentscheidungen der Runde. Leer, wenn die Runde keine Bilder zur Auswahl hatte --
   * die Ratgeber-Kette stellt Text und Bilder gemeinsam zur Freigabe, die Bilder-Kette
   * nur Bilder.
   *
   * PRAEZEDENZ fuer einen Adapter, der nur EIN Bild kennt (die Bilder-Kette hat keinen
   * Text-Zweig): `bilder[0]` schlaegt `aktion`/`gesamtFeedback`, nicht umgekehrt. Die
   * kettenweiten Felder sind der Fallback fuer eine Kette ohne Bilder (Ratgeber-Text), nicht
   * die Vorgabe fuer eine Kette, die nur Bilder fuehrt. Ein Adapter, der stattdessen vom
   * kettenweiten `aktion` liest, bekommt nie das bildspezifische Feedback zu Gesicht --
   * siehe `Frontend-Feedback Aufbereitung` in `ERGO - AWF Bilder`, gefixt 2026-09-09 nach
   * genau diesem Fehler im Whole-Branch-Review.
   */
  bilder: BildEntscheidung[];
  /**
   * Der vom Menschen bearbeitete Ratgeber-Text, vollstaendig. Fehlt, wenn nichts bearbeitet
   * wurde -- dann laesst n8n die Data-Table-Spalte unangetastet.
   */
  ratgeberText?: string;
  /**
   * Abschnitts-IDs, die sich gegenueber der Fassung aus n8n geaendert haben. Der
   * Ueberarbeitungs-Agent bekommt sie, damit er Handarbeit nicht ueberschreibt.
   */
  bearbeiteteAbschnitte?: string[];
}

// ---------------------------------------------------------------------------
// Lauf starten
// ---------------------------------------------------------------------------

/**
 * Was das Frontend beim Start schickt. Ein Typ fuer alle drei Ketten, weil die
 * Pflichtfelder sich unterscheiden, die Route aber dieselbe ist -- welche Felder je Typ
 * verlangt werden, entscheidet `pruefeStartAnfrage` im Backend, nicht der Typ hier.
 *
 * Feldnamen deutsch: Entscheidung vom 2026-09-09. `Auftrag` ist deutsch, und `GET /runs`
 * gibt `Auftrag` zurueck -- eine englische Startanfrage danebenzustellen waere Mischmasch.
 */
export interface StartAnfrage {
  typ: AuftragsTyp;
  thema: string;
  /** Nur bei `typ: 'ratgeber'`. Ersetzt das frueher separate englische Formular. */
  sprache?: Sprache;
  contentTyp?: string;
  zusatzinfos?: string;
  produkte?: string;
  zielgruppe?: string;
  longtailBevorzugt?: boolean;
  ausschlussKeywords?: string;
  quellUrl?: string;
  sharepointAblage?: boolean;
  /**
   * Nur bei `typ: 'analyse'`. Nicht leer = Verzeichnismodus, leer = Einzel-URL. Bildet
   * genau ab, woran `IF Verzeichnis vs URL` in `AWF Analyse` verzweigt. Immer ein `wert` aus
   * `RATGEBER_VERZEICHNISSE`.
   */
  verzeichnis?: string;
  /** Nur bei `typ: 'bilder'`, Pflicht. Immer einer der Werte aus `BILD_FORMATE`. */
  format?: string;
}

/**
 * Die Formate des Felds "Format" im n8n-Formular der Bilder-Kette, wortgleich: n8n vergleicht
 * den Text, nicht eine Kennung. Das erste ist die Vorgabe wie dort.
 */
export const BILD_FORMATE: readonly string[] = [
  '1.440 x 960 px',
  '21:9',
  '16:9',
  '4:3',
  '5:4',
  '1:1',
  '4:5',
  '3:4',
  '2:3',
  '9:16',
];

/**
 * Die Ratgeber-Verzeichnisse, die `AWF Analyse` kennt: die Schluessel von `productMapping` in
 * n8n. Der Wert landet dort klein geschrieben in `https://www.ergo.de/de/Ratgeber/<wert>`,
 * deshalb nur diese Liste und kein freier Text (Rueckmeldung 2026-10-13).
 */
export const RATGEBER_VERZEICHNISSE: readonly { wert: string; text: string }[] = [
  { wert: 'beruf', text: 'Beruf' },
  { wert: 'berufsunfaehigkeit', text: 'Berufsunfähigkeit' },
  { wert: 'digital', text: 'Digital' },
  { wert: 'ehe', text: 'Ehe' },
  { wert: 'familie', text: 'Familie' },
  { wert: 'finanzielle_vorsorge', text: 'Finanzielle Vorsorge' },
  { wert: 'gesundheit', text: 'Gesundheit' },
  { wert: 'kfz', text: 'Kfz' },
  { wert: 'pflege', text: 'Pflege' },
  { wert: 'reise_und_freizeit', text: 'Reise und Freizeit' },
  { wert: 'todesfall', text: 'Todesfall' },
  { wert: 'vorsorge-im-ernstfall', text: 'Vorsorge im Ernstfall' },
  { wert: 'zahngesundheit', text: 'Zahngesundheit' },
  { wert: 'zuhause', text: 'Zuhause' },
];

export function verzeichnisText(wert: string): string | undefined {
  return RATGEBER_VERZEICHNISSE.find((v) => v.wert === wert)?.text;
}

/** Siehe `Auftrag.briefing`. */
export type Briefing = Omit<StartAnfrage, 'typ'>;

/** Entscheidung ueber ein einzelnes Bild einer Feedback-Runde. */
export interface BildEntscheidung {
  index: number;
  aktion: Entscheidung;
  feedback: string;
}

/**
 * Einheitliche Fehlerform der Nutzerrouten. `code` ist der stabile Schluessel fuers
 * Frontend, `text` der Satz fuer die Anwenderin. `POST /internal/status` behaelt seine
 * einfachere Form -- sein Gegenueber ist n8n, nicht das Frontend.
 */
export interface FehlerAntwort {
  fehler: { code: string; text: string };
}

// ---------------------------------------------------------------------------
// Authentifizierung
// ---------------------------------------------------------------------------

/**
 * Rolle als Custom Claim (`rolle`), gesetzt nur ueber `tools/rolle.js`. Ohne Claim ist ein Konto
 * ein normales: Selbstregistrierung ist abgeschaltet, damit IST die Kontoexistenz die
 * Freischaltung. Seit 2026-10-15 gibt es zwei Gesamtansichten, beide nur lesend
 * (`gesamtUmfang`):
 *
 * - `admin` sieht alle Auftraege des Mandanten `ergo` (Ansprechpartnerin bei ERGO).
 * - `technik` sieht ALLE Auftraege, auch elaboratum und `system` -- fuer die Fehlersuche im
 *   Betrieb. Gilt nur an einer elaboratum-Adresse: ERGO-Konten duerfen elaboratum-Auftraege
 *   nie sehen, auch nicht bei einer versehentlich falsch vergebenen Rolle.
 */
export type Rolle = 'user' | 'admin' | 'technik';

/**
 * Der Kreis, den die Gesamtansicht der Rolle `admin` zeigt. Bewusst ein einzelner Wert: Es
 * gibt genau einen Kunden, und elaboratum taucht als Mandant absichtlich nicht auf.
 */
export type Mandant = 'ergo';

/** Die Domain, deren Adressen zu `ergo` gehoeren -- einschliesslich Subdomains. */
export const ERGO_DOMAIN = 'ergo.de';

/** Die Domain der Betreiber; nur sie tragen die Rolle `technik`. Ohne Subdomains. */
export const ELABORATUM_DOMAIN = 'elaboratum.de';

/**
 * Die Domain hinter dem LETZTEN `@`. Ein blosses `includes('ergo.de')` liesse auch
 * `x@nicht-ergo.de` oder `ergo.de@elaboratum.de` durch.
 */
function domainVon(email: string | null | undefined): string | null {
  if (!email) return null;
  const at = email.lastIndexOf('@');
  if (at < 1) return null;
  return email
    .slice(at + 1)
    .trim()
    .toLowerCase();
}

/** Der Mandant zu einer Adresse: `ergo` fuer ergo.de, exakt oder als Subdomain. */
export function mandantVon(email: string | null | undefined): Mandant | undefined {
  const domain = domainVon(email);
  if (!domain) return undefined;
  return domain === ERGO_DOMAIN || domain.endsWith(`.${ERGO_DOMAIN}`) ? 'ergo' : undefined;
}

/**
 * Exakt elaboratum.de. Dieselbe Pruefung steht in `firestore.rules` (Funktion `technik`); dort
 * als regulaerer Ausdruck auf die kleingeschriebene Adresse, der ebenfalls am Ende verankert ist.
 */
export function istElaboratum(email: string | null | undefined): boolean {
  return domainVon(email) === ELABORATUM_DOMAIN;
}

/** Was die Gesamtansicht zeigt: nur ERGO-Auftraege oder alle. */
export type GesamtUmfang = 'ergo' | 'alle';

/**
 * Die EINE Regel, welche Gesamtansicht ein Konto hat -- Backend (`darfLesen`) und Frontend
 * (Umschalter) lesen sie von hier, die Rules spiegeln sie. `null`: keine, nur eigene Auftraege.
 */
export function gesamtUmfang(
  rolle: unknown,
  email: string | null | undefined,
): GesamtUmfang | null {
  if (rolle === 'technik' && istElaboratum(email)) return 'alle';
  if (rolle === 'admin') return 'ergo';
  return null;
}

/**
 * Die Teile des Firebase-ID-Tokens, auf die wir uns stuetzen.
 *
 * `signInSecondFactor` ist der Kern der Zugriffskontrolle: Firebase kennt keinen Schalter
 * "MFA fuer alle Pflicht", die Durchsetzung ist Sache der Anwendung. Das Backend weist
 * jedes Token OHNE diesen Wert mit 403 ab -- ausgenommen sind allein die Routen zur
 * Einrichtung des zweiten Faktors. Eine reine Client-Logik waere wertlos, weil sie sich
 * umgehen laesst.
 */
export interface TokenAnspruch {
  uid: string;
  email: string;
  emailVerified: boolean;
  signInSecondFactor?: 'totp' | 'phone';
  rolle?: Rolle;
  /** Anzeigename aus dem Token (`name`), falls am Konto gesetzt. */
  name?: string;
}

export interface RatgeberAbschnitt {
  abschnittId: string;
  ueberschrift: string;
  ebene: number;
  absaetze: { absatzNummer: number; text: string }[];
}

export interface RatgeberStruktur {
  version: number;
  abschnitte: RatgeberAbschnitt[];
}

/**
 * Der PDP-Kontext der Freigabeseite (Spec 2026-10-12, Abschnitt 5.4). n8n liefert ihn nur bei
 * `content_type === 'pdp'` aus `Textantwort bauen`. `fachlichFreigegeben: null` heisst "nicht
 * geprueft". `anhang` ist der redaktionelle Analyseanhang (Markdown): nur lesbar, nie Teil des
 * Entwurfs oder des Entscheidungs-Payloads.
 */
export interface PdpKontext {
  fachlichFreigegeben: boolean | null;
  gruende: string[];
  anhang: string;
  sharepoint: 'ja' | 'nein' | null;
}

/** Antwort von `GET /runs/:id/text`. */
export interface TextAntwort {
  text: string;
  struktur?: RatgeberStruktur;
  /**
   * `url` ist die Originaladresse beim Bildgenerator -- sie bleibt der Wert, mit dem der
   * Korrekturzweig in n8n weiterarbeitet. Anzeigen darf die Oberflaeche sie nicht: Der
   * Host steht nicht auf der Allowlist `BILD_HOSTS`, die auch fuer Bilder im
   * LLM-erzeugten Text gilt. `vorschauUrl` traegt deshalb eine Drive-Adresse, die dort
   * erlaubt und als Thumbnail rund 30-mal kleiner ist. Optional, weil Laeufe von vor
   * diesem Umbau sie nicht haben.
   */
  bilder: { index: number; url: string; name?: string; typ?: string; vorschauUrl?: string }[];
  /**
   * Nur bei PDP-Auftraegen. Fehlt er oder ist er ungueltig, laesst das Backend ihn weg -- die
   * Freigabe funktioniert dann ohne Kontext-Kasten und Anhang (Spec Abschnitt 6).
   */
  kontext?: PdpKontext;
}

/**
 * Die Runde, zu der jetzt eine Entscheidung angenommen wird -- oder `null`. Die EINE Regel dafuer
 * (Spec 2026-09-29-absende-rennen Sec4.2): Backend (`beanspracheRunde`, `pruefeEntscheidung`,
 * `speichereEntwurf`) und Frontend (Sperre der Freigabeseite) fragen nur noch sie.
 *
 * Offen heisst: Der Lauf wartet, n8n hat die Fassung DIESER Runde vorgelegt, und sie ist noch
 * nicht entschieden. Fehlt `vorgelegteRunde` (Altbestand, noch nie hochgezaehlt), gilt die Runde
 * wie vor diesem Feld als offen.
 *
 * Bekanntes Restrisiko: n8n schickt in den wartet-Meldungen keine echte Runde (Ratgeber: keine,
 * Bilder: Literal 1). Eine verspaetete oder doppelte `wartet_auf_freigabe`-Meldung der Vorrunde,
 * die NACH `erhoeheRunde` eintrifft, wuerde deshalb die neue Runde als vorgelegt markieren.
 * Gemessen nie aufgetreten (Learnings 2026-09-29), nicht schlechter als vorher; die echte
 * Loesung waere eine Rundenangabe aus n8n. Vom Auftraggeber am 2026-09-29 so akzeptiert.
 */
export function offeneRunde(
  auftrag: Pick<Auftrag, 'status' | 'runde' | 'entschiedeneRunde' | 'vorgelegteRunde'>,
): number | null {
  const r = auftrag.runde ?? 1;
  if (auftrag.status !== 'wartet_auf_freigabe') return null;
  if (auftrag.entschiedeneRunde === r) return null;
  if (auftrag.vorgelegteRunde !== undefined && auftrag.vorgelegteRunde !== r) return null;
  return r;
}

/**
 * Ab wann ein Auftrag als stehen geblieben gilt: doppelt so lang wie die laengste beobachtete
 * Laufzeit ohne Zwischenmeldung. Ratgeber, PDP und Bilder melden nach spaetestens rund
 * 20 Minuten einen neuen Stand; eine Verzeichnis-Analyse lief gemessen 92 Minuten am Stueck
 * (Codex-Review PR #73, KRITISCH: 45 Minuten haetten eine laufende Analyse loeschbar gemacht).
 * Frontend (Hinweis "kommt gerade nicht weiter") und Backend (Loeschen) lesen dieselbe Zahl.
 */
export const STILLSTAND_NACH_MS = 45 * 60 * 1000;
const STILLSTAND_ANALYSE_MS = 3 * 60 * 60 * 1000;

export function stillstandNachMs(typ: AuftragsTyp): number {
  return typ === 'analyse' ? STILLSTAND_ANALYSE_MS : STILLSTAND_NACH_MS;
}

const LOESCHBAR_END: readonly AuftragsStatus[] = ['fertig', 'fehlgeschlagen', 'abgebrochen'];
const LOESCHBAR_NIE: readonly AuftragsStatus[] = ['wartet_auf_freigabe', 'wartet_auf_entscheidung'];

/** Laeuft nicht mehr erkennbar: kein Endzustand, kein Warten, und zu lange kein neuer Stand. */
export function stehtStill(
  auftrag: Pick<Auftrag, 'typ' | 'status' | 'aktualisiertAm'>,
  jetzt: number,
): boolean {
  if (LOESCHBAR_END.includes(auftrag.status) || LOESCHBAR_NIE.includes(auftrag.status)) {
    return false;
  }
  const zeitpunkt = Date.parse(auftrag.aktualisiertAm);
  return !Number.isNaN(zeitpunkt) && jetzt - zeitpunkt > stillstandNachMs(auftrag.typ);
}

/**
 * Loeschbar ist ein abgeschlossener Auftrag und einer, der stehen geblieben ist (Rueckmeldung
 * 2026-10-13: sonst bleibt ein haengender Auftrag fuer immer in der Uebersicht). Meldet n8n zu
 * einem geloeschten doch noch etwas, haelt der Grabstein in `geloeschte_auftraege` ihn draussen.
 * Ein wartender Auftrag ruht dagegen zu Recht und wartet auf den Menschen: nicht loeschbar.
 */
export function istLoeschbar(
  auftrag: Pick<Auftrag, 'typ' | 'status' | 'aktualisiertAm'>,
  jetzt: number,
): boolean {
  return LOESCHBAR_END.includes(auftrag.status) || stehtStill(auftrag, jetzt);
}

/**
 * Darf das am PDP-Ursprung vermerkte Kind "volle Analyse" (`volleAnalyseRunId`) durch ein neues
 * ersetzt werden (Spec 2026-11-03-kein-bedarf, Abschnitt 6)? Ja, wenn es gescheitert ist, vom
 * Sweep abgebrochen wurde, von der Person ausgeblendet ("geloescht") wurde oder gar nicht mehr da
 * ist -- sonst waere der Ursprung eine Sackgasse. Backend (`legeVolleAnalyseAn`) und Frontend (Button
 * statt Link) nutzen dieselbe Regel.
 */
export function volleAnalyseKindErsetzbar(
  kind: Pick<Auftrag, 'status' | 'ausgeblendetAm'> | null | undefined,
): boolean {
  if (!kind) return true;
  return (
    kind.status === 'fehlgeschlagen' ||
    kind.status === 'abgebrochen' ||
    kind.ausgeblendetAm !== undefined
  );
}

// ---------------------------------------------------------------------------
// Nutzungsbedingungen
// ---------------------------------------------------------------------------

/**
 * Die Nutzungsbedingungen, die jede Person einmal bestaetigt, bevor sie die Oberflaeche benutzt.
 * Die Bestaetigung liegt mit Server-Zeitstempel in Firestore und ist der Beleg fuer AI-1-2
 * ("Nutzer informiert, Referenz") -- staerker als ein abgelegtes PDF, weil sie je Person und je
 * Fassung nachweist, WER WANN WELCHEN Text bestaetigt hat.
 *
 * Hier und nicht im Frontend, weil das Backend einen Hash genau dieses Textes mitspeichert: So
 * belegt der Eintrag den Wortlaut, nicht nur eine Versionsnummer.
 *
 * Text und `version` gehoeren zusammen. Wer ein Wort aendert, hebt die Version an -- dann
 * bestaetigen alle neu. Der Waechter dafuer ist `nutzungsbedingungen.test.ts` im Backend: er
 * pinnt den Hash je Version.
 */
export const NUTZUNGSBEDINGUNGEN = {
  version: '2026-10-16',
  titel: 'Nutzungsbedingungen der Content Engine',
  absaetze: [
    'Die Content Engine erstellt Ratgebertexte, Bilder und Seitenanalysen mithilfe künstlicher Intelligenz (KI). KI-generierte Inhalte können sachlich falsch, unvollständig, veraltet oder rechtlich unzulässig sein.',
    'Prüfen Sie jeden Inhalt vor einer Veröffentlichung fachlich, sprachlich und rechtlich. Mit Ihrer Freigabe übernehmen Sie die Verantwortung für den Inhalt.',
    'Geben Sie keine personenbezogenen Daten von Kundinnen und Kunden, keine Gesundheitsdaten und keine vertraulichen Geschäftsinformationen in die Eingabefelder ein.',
    'Nutzen Sie die Content Engine nur für Aufgaben der ERGO-Ratgeberredaktion. Ihr Zugang ist persönlich und darf nicht weitergegeben werden.',
    'Ihre Bestätigung wird mit Zeitpunkt und Fassung dieser Bedingungen gespeichert.',
  ],
} as const;

/** Antwort von `GET /nutzungsbedingungen` und `POST /nutzungsbedingungen`. */
export interface NutzungsbedingungenStand {
  /** Die Fassung, die das Backend gerade verlangt. */
  version: string;
  /** ISO-Zeitpunkt der Bestaetigung DIESER Fassung, `null` solange sie aussteht. */
  bestaetigtAm: string | null;
}
